import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { splitIngredient } from '@/lib/cookbook/ingredients'
import { contentsPageNumber, firstPicture, leavesOf, type Section } from '@/lib/cookbook/pages'
import { pictureSrcSet, pictureUrl, type Cookbook, type CookbookPage, type Picture } from '@/lib/cookbooks'
import { lines } from '@/lib/recipes'
import { cn } from '@/lib/utils'

// The pages of a cookbook as a printed one would have them (styles/cookbook.css): the cover on its own, the contents
// across a spread, then each recipe across a spread, its picture on the left and the recipe on the right.

export type Side = 'left' | 'right'

export function Page({
  side,
  folio,
  className,
  children,
}: {
  side: Side
  folio?: number
  className?: string
  children: ReactNode
}) {
  return (
    <div className="cb-sizer">
      <div className={cn('cb-page', side === 'left' ? 'is-left' : 'is-right', className)}>
        {children}
        {folio !== undefined && <span className="cb-folio">{folio}</span>}
      </div>
    </div>
  )
}

export function PictureImage({ picture, alt, sizes }: { picture: Picture; alt: string; sizes: string }) {
  return (
    <img
      src={pictureUrl(picture, 'display')}
      srcSet={pictureSrcSet(picture)}
      sizes={sizes}
      width={picture.width}
      height={picture.height}
      alt={alt}
      loading="lazy"
      decoding="async"
    />
  )
}

// The cover: the cookbook's name, a round photo (its first picture), and who made it. The shelf shows the same cover,
// small.
export function Cover({
  title,
  subtitle,
  by,
  picture,
  sizes = '40vw',
}: {
  title: string
  subtitle: string
  by?: string
  picture?: Picture | null
  sizes?: string
}) {
  return (
    <Page side="right" className="cb-cover">
      <div className="cb-in">
        <h1 className="cb-serif cb-cover-title">{title}</h1>
        {subtitle && <p className="cb-cover-subtitle cb-serif">{subtitle}</p>}
        {picture ? (
          <div className="cb-plate">
            <PictureImage picture={picture} alt="" sizes={sizes} />
          </div>
        ) : (
          <div className="mt-auto" />
        )}
        {by && (
          <p className="cb-byline cb-tracked">
            <span>by {by}</span>
          </p>
        )}
      </div>
    </Page>
  )
}

// The dedication, alone on a right-hand page a third of the way down, as a printed book sets it.
export function Dedication({ text }: { text: string }) {
  return (
    <Page side="right" className="cb-dedication">
      <div className="cb-in">
        <p className="cb-serif">{text}</p>
      </div>
    </Page>
  )
}

export function ContentsPicture({ cookbook, sizes = '40vw' }: { cookbook: Cookbook; sizes?: string }) {
  const picture = cookbook.pages.filter((x) => x.picture).at(1)?.picture ?? firstPicture(cookbook)
  return (
    <Page side="left" folio={contentsPageNumber(cookbook)}>
      <h2 className="cb-serif cb-contents-title">Contents</h2>
      {picture && (
        <div className="cb-contents-photo">
          <PictureImage picture={picture} alt="" sizes={sizes} />
        </div>
      )}
    </Page>
  )
}

// The recipes in order with their page numbers, under their sections when the book has them. In the reader each is a
// link to its spread; `href` is given the stop in the book (leavesOf) to go to.
export function ContentsList({ cookbook, href }: { cookbook: Cookbook; href?: (leaf: number) => string }) {
  const leaves = leavesOf(cookbook)
  const sections = leaves.some((x) => x.kind === 'section')
  const dense = cookbook.pages.length + (sections ? leaves.filter((x) => x.kind === 'section').length : 0) > 16
  return (
    <Page side="right" folio={contentsPageNumber(cookbook) + 1}>
      <div className="cb-in">
        <ol className={cn('cb-toc', dense && 'is-dense')} aria-label="Contents">
          {leaves.map((leaf, n) => {
            if (leaf.kind === 'section')
              return (
                <li key={`section-${leaf.section.first}`} className="cb-toc-section">
                  {href ? <Link to={href(n)}>{leaf.section.title}</Link> : leaf.section.title}
                </li>
              )
            if (leaf.kind !== 'recipe') return null
            const page = cookbook.pages[leaf.index]!
            const row = (
              <>
                <span className="cb-toc-name">
                  {page.recipe.title}
                  {!dense && page.recipe.from && <span className="cb-toc-from">From {page.recipe.from.name}</span>}
                </span>
                <span className="cb-toc-n">{leaf.folio}</span>
              </>
            )
            return (
              <li key={page.recipe.id}>
                {href ? <Link to={href(n)}>{row}</Link> : <span className="cb-toc-row">{row}</span>}
              </li>
            )
          })}
        </ol>
      </div>
    </Page>
  )
}

// A section's opening page: its name alone, on a right-hand page, the way a printed cookbook starts its chapters.
export function SectionPage({ section, folio }: { section: Section; folio?: number }) {
  return (
    <Page side="right" folio={folio} className="cb-section">
      <div className="cb-in">
        <h2 className="cb-serif">{section.title}</h2>
        <p className="cb-tracked">
          {section.count} {section.count === 1 ? 'recipe' : 'recipes'}
        </p>
      </div>
    </Page>
  )
}

export function RecipePicture({ page, folio, sizes = '40vw' }: { page: CookbookPage; folio: number; sizes?: string }) {
  const { recipe, picture } = page
  if (!picture)
    return (
      <Page side="left" folio={folio} className="cb-title-page">
        <div className="cb-in">
          {recipe.from && <p className="cb-tracked">FROM {recipe.from.name.toUpperCase()}</p>}
          <h2 className="cb-serif">{recipe.title}</h2>
          <span className="cb-rule" aria-hidden="true" />
        </div>
      </Page>
    )
  return (
    <Page side="left">
      <div className="cb-bleed">
        <PictureImage picture={picture} alt={recipe.title} sizes={sizes} />
      </div>
    </Page>
  )
}

export function RecipeText({ page, folio, running }: { page: CookbookPage; folio: number; running: string }) {
  const { recipe } = page
  const ingredients = lines(recipe.ingredients).map(splitIngredient)
  const steps = lines(recipe.steps)
  const notes = recipe.notes.trim()
  const words = steps.join(' ').length
  const method = steps.length > 0 || !!notes
  return (
    <Page side="right" folio={folio}>
      <div className="cb-in">
        <p className="cb-tracked cb-running">{running}</p>
        <header className="cb-recipe-head">
          <h2 className="cb-serif cb-recipe-title">{recipe.title}</h2>
          <Facts page={page} />
        </header>
        {/* Like a menu: the ingredients in a narrow column with the preparation beside them. With only one of the
            two, it has the page's whole width. */}
        <div className={cn('cb-body', !(ingredients.length && method) && 'is-single')}>
          {ingredients.length > 0 && (
            <section>
              <h3 className="cb-heading">Ingredients</h3>
              <ul className={cn('cb-ingredients', !method && ingredients.length > 7 && 'is-two')}>
                {ingredients.map((x, i) => (
                  <li key={i} className={cn(!x.amount && 'is-whole')}>
                    {x.amount && <span className="cb-amount">{x.amount}</span>}
                    <span>{x.item}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {method && (
            <div>
              {steps.length > 0 && (
                <section>
                  <h3 className="cb-heading">Preparation</h3>
                  <ol
                    className={cn(
                      'cb-steps',
                      steps.length === 1 && 'is-one',
                      !ingredients.length && words > 900 && 'is-two',
                    )}
                  >
                    {steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </section>
              )}
              {notes && (
                <section>
                  <h3 className="cb-heading">Notes</h3>
                  <p className="cb-notes whitespace-pre-line">{notes}</p>
                </section>
              )}
            </div>
          )}
        </div>
        {!ingredients.length && !steps.length && !notes && (
          <p className="cb-notes mt-8">Only the photo of this recipe’s card is kept so far.</p>
        )}
      </div>
    </Page>
  )
}

// Only what the family recorded: who it's from and the book it's kept in. Times and servings aren't kept, so they
// aren't shown.
function Facts({ page }: { page: CookbookPage }) {
  const { from, book } = page.recipe
  return (
    <p className="cb-facts cb-tracked">
      {from && (
        <span>
          FROM <b>{from.name}</b>
        </span>
      )}
      {book && (
        <span>
          BOOK <b>{book.title}</b>
        </span>
      )}
    </p>
  )
}
